import type { CraftingService, InventoryManager } from '../items/InventoryManager';
import { ITEM_CATALOG, ItemId, type Recipe } from '../items/catalog';
import { GameState } from '../types';
import { itemIconHtml, itemShortName, recipeShortName } from './itemIcons';

export interface InventoryUIHost {
  inventory: InventoryManager;
  crafting: CraftingService;
  getGameState(): GameState;
  getWallet(): number;
  onEquipChanged(): void;
  onCrafted(recipeId: string, resultItemId: string): void;
  requestPointerLock(): void;
  exitPointerLock(): void;
}

type RecipeLane = 'pickaxe' | 'sword' | 'other';

function recipeLane(recipe: Recipe): RecipeLane {
  if (recipe.result.startsWith('pickaxe_')) return 'pickaxe';
  if (recipe.result.startsWith('sword_') || recipe.result === 'blaster_plasma') return 'sword';
  return 'other';
}

/**
 * Tab-toggled inventory / forge panel + always-on hotbar strip.
 * Forge UI highlights the next upgrade with big kid-friendly icons.
 */
export class InventoryUI {
  private readonly root: HTMLDivElement;
  private readonly hotbar: HTMLDivElement;
  private readonly host: InventoryUIHost;
  private open = false;
  private unsub: (() => void) | null = null;

  constructor(parent: HTMLElement, host: InventoryUIHost) {
    this.host = host;

    this.hotbar = document.createElement('div');
    this.hotbar.id = 'hotbar';
    parent.appendChild(this.hotbar);

    this.root = document.createElement('div');
    this.root.id = 'inv-overlay';
    this.root.innerHTML = `
      <div class="inv-panel" role="dialog" aria-label="Forge">
        <header class="inv-header">
          <div class="inv-title-block">
            <h2>Forge</h2>
            <p class="inv-subtitle">Make your next upgrade!</p>
          </div>
          <div class="inv-header-actions">
            <div class="inv-wallet-chip" data-wallet></div>
            <button type="button" class="inv-close" data-close aria-label="Close forge">✕</button>
          </div>
        </header>

        <section class="forge-next" data-forge-next></section>

        <section class="forge-section">
          <h3>More Upgrades</h3>
          <div class="forge-grid" data-list="recipes"></div>
        </section>

        <div class="inv-bottom">
          <section class="forge-section forge-bag">
            <h3>My Stuff</h3>
            <div class="mat-chips" data-list="materials"></div>
            <div class="gear-row" data-list="gear"></div>
          </section>
          <section class="forge-section forge-hot">
            <h3>Hotbar <small>tap to clear</small></h3>
            <div class="inv-hotbar-edit" data-list="hotbar-edit"></div>
          </section>
        </div>
      </div>
    `;
    parent.appendChild(this.root);

    this.root.addEventListener('pointerdown', (e) => {
      if (e.target === this.root) {
        e.preventDefault();
        this.setOpen(false);
        return;
      }
      const closeBtn = (e.target as HTMLElement | null)?.closest?.('[data-close]');
      if (closeBtn && this.root.contains(closeBtn)) {
        e.preventDefault();
        this.setOpen(false);
      }
    });

    this.root.addEventListener('pointerdown', (e) => {
      const craftBtn = (e.target as HTMLElement | null)?.closest?.('[data-craft]');
      if (!(craftBtn instanceof HTMLElement) || !this.root.contains(craftBtn)) return;
      if (craftBtn.hasAttribute('disabled')) return;
      e.preventDefault();
      e.stopPropagation();
      this.tryCraft(craftBtn.dataset.craft!);
    });

    this.unsub = host.inventory.onChange(() => this.refresh());
    this.hotbar.addEventListener('pointerdown', (e) => {
      const slot = (e.target as HTMLElement | null)?.closest?.('.hot-slot');
      if (!(slot instanceof HTMLElement) || !this.hotbar.contains(slot)) return;
      const index = Array.prototype.indexOf.call(this.hotbar.children, slot);
      if (index < 0) return;
      e.preventDefault();
      e.stopPropagation();
      if (this.host.inventory.setActiveHotbar(index)) {
        this.host.onEquipChanged();
        this.renderHotbar();
      }
    });
    this.injectStyles();
    this.refresh();
  }

  get isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    this.setOpen(!this.open);
  }

  setOpen(open: boolean): void {
    if (this.open === open) return;
    this.open = open;
    this.root.classList.toggle('open', open);
    if (open) {
      this.host.exitPointerLock();
      this.refresh();
    } else if (this.host.getGameState() === GameState.Diving) {
      this.host.requestPointerLock();
    }
  }

  refresh(): void {
    this.renderHotbar();
    if (!this.open) return;

    const nextEl = this.root.querySelector('[data-forge-next]') as HTMLElement;
    const recipesEl = this.root.querySelector('[data-list="recipes"]') as HTMLElement;
    const matsEl = this.root.querySelector('[data-list="materials"]') as HTMLElement;
    const gearEl = this.root.querySelector('[data-list="gear"]') as HTMLElement;
    const hotEdit = this.root.querySelector('[data-list="hotbar-edit"]') as HTMLElement;
    const walletEl = this.root.querySelector('[data-wallet]') as HTMLElement;

    const { hero, rest } = this.partitionRecipes();
    nextEl.innerHTML = hero ? this.renderHeroCard(hero) : this.renderAllDoneHero();
    recipesEl.innerHTML = rest.length
      ? rest.map((r) => this.renderRecipeCard(r)).join('')
      : `<div class="inv-empty">No more upgrades right now</div>`;
    matsEl.innerHTML = this.renderMaterialChips();
    gearEl.innerHTML = this.renderGearList(this.host.inventory.listOwnedGear());
    hotEdit.innerHTML = this.renderHotbarEdit();
    walletEl.innerHTML = `<span class="coin">¢</span> ${this.host.getWallet()}`;

    gearEl.querySelectorAll('[data-equip]').forEach((btn) => {
      btn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        const id = (btn as HTMLElement).dataset.equip as ItemId;
        this.equipToHotbar(id);
      });
    });

    hotEdit.querySelectorAll('[data-hot-slot]').forEach((btn) => {
      btn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        const index = Number((btn as HTMLElement).dataset.hotSlot);
        const hb = this.host.inventory.getHotbar();
        if (hb.slots[index]) {
          this.host.inventory.assignHotbar(index, null);
          this.host.onEquipChanged();
          this.refresh();
        }
      });
    });
  }

  dispose(): void {
    this.unsub?.();
    this.root.remove();
    this.hotbar.remove();
  }

  private tryCraft(recipeId: string): void {
    const result = this.host.crafting.craft(recipeId);
    const btn = this.root.querySelector(`[data-craft="${recipeId}"]`) as HTMLElement | null;
    if (!result.ok) {
      btn?.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' }, { transform: 'translateX(0)' }],
        { duration: 240 },
      );
    } else if (result.recipe) {
      this.host.onCrafted(result.recipe.id, result.recipe.result);
    }
    this.host.onEquipChanged();
    this.refresh();
  }

  private equipToHotbar(id: ItemId): void {
    const hb = this.host.inventory.getHotbar();
    let slot = hb.slots.findIndex((s) => s === null);
    if (slot < 0) slot = hb.activeIndex;
    this.host.inventory.assignHotbar(slot, id);
    this.host.inventory.setActiveHotbar(slot);
    this.host.onEquipChanged();
    this.refresh();
  }

  /** Next upgrade per ladder, preferring craftable pickaxe → sword → other. */
  private partitionRecipes(): { hero: Recipe | null; rest: Recipe[] } {
    const all = this.host.crafting.listRecipes();
    const available = all.filter((r) => !this.host.inventory.ownsGear(r.result));

    const nextByLane = new Map<RecipeLane, Recipe>();
    for (const recipe of available) {
      const lane = recipeLane(recipe);
      if (nextByLane.has(lane)) continue;
      // Only show ladder step if prerequisite is met (or none).
      if (recipe.requiresOwned && !this.host.inventory.ownsGear(recipe.requiresOwned)) {
        continue;
      }
      nextByLane.set(lane, recipe);
    }

    const ordered: Recipe[] = [];
    for (const lane of ['pickaxe', 'sword', 'other'] as RecipeLane[]) {
      const r = nextByLane.get(lane);
      if (r) ordered.push(r);
    }

    // Also include other unlocked (prereq met) recipes not already in next-by-lane
    // e.g. mace while sword path is active — keep as secondary cards.
    for (const recipe of available) {
      if (recipe.requiresOwned && !this.host.inventory.ownsGear(recipe.requiresOwned)) continue;
      if (ordered.some((o) => o.id === recipe.id)) continue;
      ordered.push(recipe);
    }

    // Prefer a craftable recipe as hero; else first next upgrade.
    const craftable = ordered.find((r) => this.host.crafting.canCraft(r).ok);
    const hero = craftable ?? ordered[0] ?? null;
    const rest = ordered.filter((r) => r.id !== hero?.id);
    return { hero, rest };
  }

  private renderAllDoneHero(): string {
    return `
      <div class="forge-hero done">
        <div class="forge-hero-icon">${itemIconHtml(ItemId.PickaxePlasma, 88)}</div>
        <div class="forge-hero-body">
          <span class="forge-badge">All set!</span>
          <h3>You forged everything</h3>
          <p>Keep mining and fighting with your best gear.</p>
        </div>
      </div>
    `;
  }

  private renderHeroCard(recipe: Recipe): string {
    const check = this.host.crafting.canCraft(recipe);
    const result = ITEM_CATALOG[recipe.result];
    const progress = this.costProgress(recipe);
    return `
      <div class="forge-hero ${check.ok ? 'ready' : 'locked'}">
        <div class="forge-hero-icon">${itemIconHtml(recipe.result, 96)}</div>
        <div class="forge-hero-body">
          <span class="forge-badge">${check.ok ? 'Ready to make!' : 'Next upgrade'}</span>
          <h3>${itemShortName(recipe.result)}</h3>
          <p>${result.description}</p>
          <div class="cost-chips">${this.renderCostChips(recipe)}</div>
          ${
            check.ok
              ? `<button type="button" class="craft-btn craft-btn-hero" data-craft="${recipe.id}">Make It!</button>`
              : `<div class="forge-progress"><div class="forge-progress-bar" style="width:${progress}%"></div></div>
                 <div class="reason">${check.reason ?? 'Keep collecting!'}</div>`
          }
        </div>
      </div>
    `;
  }

  private renderRecipeCard(recipe: Recipe): string {
    const check = this.host.crafting.canCraft(recipe);
    return `
      <div class="forge-card ${check.ok ? 'can' : 'cant'}">
        <div class="forge-card-icon">${itemIconHtml(recipe.result, 56)}</div>
        <div class="forge-card-name">${recipeShortName(recipe.name)}</div>
        <div class="cost-chips compact">${this.renderCostChips(recipe)}</div>
        ${
          check.ok
            ? `<button type="button" class="craft-btn" data-craft="${recipe.id}">Make</button>`
            : `<div class="reason">${this.shortReason(check.reason)}</div>`
        }
      </div>
    `;
  }

  private shortReason(reason?: string): string {
    if (!reason) return 'Not yet';
    if (reason.startsWith('Need ')) return reason.replace('Need ', 'Need ');
    if (reason.startsWith('Requires ')) return 'Need better gear';
    if (reason === 'Already owned.') return 'Owned';
    return reason;
  }

  private renderCostChips(recipe: Recipe): string {
    const chips = recipe.costs.map((c) => {
      const have = this.host.inventory.getAvailable(c.itemId);
      const ok = have >= c.amount;
      return `
        <span class="cost-chip ${ok ? 'ok' : 'miss'}">
          ${itemIconHtml(c.itemId, 22)}
          <span class="cost-amt">${have}/${c.amount}</span>
        </span>`;
    });
    if (recipe.creditCost > 0) {
      const ok = this.host.getWallet() >= recipe.creditCost;
      chips.push(`
        <span class="cost-chip ${ok ? 'ok' : 'miss'}">
          <span class="coin-chip">¢</span>
          <span class="cost-amt">${this.host.getWallet()}/${recipe.creditCost}</span>
        </span>`);
    }
    return chips.join('');
  }

  private costProgress(recipe: Recipe): number {
    let have = 0;
    let need = 0;
    for (const c of recipe.costs) {
      need += c.amount;
      have += Math.min(this.host.inventory.getAvailable(c.itemId), c.amount);
    }
    if (recipe.creditCost > 0) {
      need += recipe.creditCost;
      have += Math.min(this.host.getWallet(), recipe.creditCost);
    }
    if (need <= 0) return 100;
    return Math.round((have / need) * 100);
  }

  private renderMaterialChips(): string {
    const run = this.host.inventory.listRunSlots();
    const bank = this.host.inventory.listBankSlots();
    const totals = new Map<ItemId, number>();
    for (const s of [...run, ...bank]) {
      totals.set(s.itemId, (totals.get(s.itemId) ?? 0) + s.amount);
    }
    if (totals.size === 0) return `<div class="inv-empty">Mine blocks to collect stuff!</div>`;
    return [...totals.entries()]
      .map(
        ([id, amount]) => `
        <div class="mat-chip" title="${ITEM_CATALOG[id].name}">
          ${itemIconHtml(id, 28)}
          <span class="mat-name">${itemShortName(id)}</span>
          <strong>×${amount}</strong>
        </div>`,
      )
      .join('');
  }

  private renderGearList(ids: ItemId[]): string {
    if (ids.length === 0) return `<div class="inv-empty">No gear yet</div>`;
    return ids
      .map(
        (id) => `
        <button type="button" class="gear-btn" data-equip="${id}" title="Equip ${ITEM_CATALOG[id].name}">
          ${itemIconHtml(id, 36)}
          <span>${itemShortName(id)}</span>
        </button>`,
      )
      .join('');
  }

  private renderHotbarEdit(): string {
    const hb = this.host.inventory.getHotbar();
    return hb.slots
      .map((id, i) => {
        const active = i === hb.activeIndex ? ' active' : '';
        if (!id) {
          return `<button type="button" class="hot-edit empty${active}" data-hot-slot="${i}"><span class="key">${i + 1}</span><span class="hot-empty">+</span></button>`;
        }
        return `<button type="button" class="hot-edit${active}" data-hot-slot="${i}" style="--accent:${ITEM_CATALOG[id].color}">
          <span class="key">${i + 1}</span>
          ${itemIconHtml(id, 32)}
        </button>`;
      })
      .join('');
  }

  private renderHotbar(): void {
    const hb = this.host.inventory.getHotbar();
    this.hotbar.innerHTML = hb.slots
      .map((id, i) => {
        const active = i === hb.activeIndex ? ' active' : '';
        if (!id) {
          return `<div class="hot-slot${active}"><span class="key">${i + 1}</span></div>`;
        }
        const def = ITEM_CATALOG[id];
        return `<div class="hot-slot${active}" style="--accent:${def.color}">
          <span class="key">${i + 1}</span>
          ${itemIconHtml(id, 28)}
        </div>`;
      })
      .join('');
  }

  private injectStyles(): void {
    if (document.getElementById('inv-styles')) return;
    const style = document.createElement('style');
    style.id = 'inv-styles';
    style.textContent = `
      .item-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        filter: drop-shadow(0 2px 2px rgba(0,0,0,0.35));
      }
      .item-icon svg { width: 100%; height: 100%; display: block; }

      #hotbar {
        position: absolute;
        left: 50%;
        bottom: 18px;
        transform: translateX(-50%);
        display: flex;
        gap: 6px;
        pointer-events: none;
        z-index: 5;
      }
      .hot-slot {
        min-width: 56px;
        height: 52px;
        padding: 4px 6px;
        border: 2px solid rgba(232,238,245,0.25);
        background: rgba(8,12,18,0.78);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        font-size: 11px;
        box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--accent, transparent) 40%, transparent);
        border-radius: 10px;
      }
      .hot-slot.active {
        border-color: #e8eef5;
        background: rgba(20,28,40,0.95);
      }
      .hot-slot .key { opacity: 0.5; font-size: 9px; line-height: 1; }

      #inv-overlay {
        position: absolute;
        inset: 0;
        display: none;
        align-items: stretch;
        justify-content: center;
        padding: max(8px, env(safe-area-inset-top)) 10px max(8px, env(safe-area-inset-bottom));
        background: rgba(4,8,12,0.78);
        z-index: 20;
        pointer-events: auto;
        -webkit-tap-highlight-color: transparent;
      }
      #inv-overlay.open { display: flex; }
      .inv-panel {
        width: min(720px, 100%);
        max-height: 100%;
        overflow: auto;
        background: linear-gradient(165deg, #1a2430 0%, #0c121a 55%, #101820 100%);
        border: 2px solid rgba(232,238,245,0.16);
        border-radius: 18px;
        padding: 14px 14px 18px;
        color: #e8eef5;
        font-family: ui-rounded, "Trebuchet MS", "Segoe UI", sans-serif;
        overscroll-behavior: contain;
      }
      .inv-header {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: flex-start;
        margin-bottom: 12px;
      }
      .inv-title-block h2 {
        margin: 0;
        font-size: 28px;
        font-weight: 800;
        letter-spacing: 0.02em;
        color: #ffb347;
        text-shadow: 0 2px 0 rgba(0,0,0,0.35);
      }
      .inv-subtitle {
        margin: 2px 0 0;
        font-size: 13px;
        opacity: 0.7;
      }
      .inv-header-actions { display: flex; align-items: center; gap: 8px; }
      .inv-wallet-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 6px 10px;
        border-radius: 999px;
        background: rgba(255, 200, 80, 0.12);
        border: 1px solid rgba(255, 200, 80, 0.35);
        font-weight: 700;
        font-size: 14px;
        color: #ffd27a;
      }
      .inv-wallet-chip .coin { font-size: 16px; }
      .inv-close {
        width: 40px;
        height: 40px;
        border-radius: 12px;
        border: 2px solid rgba(232,238,245,0.28);
        background: rgba(232,238,245,0.08);
        color: #e8eef5;
        font: inherit;
        font-size: 18px;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }
      .inv-close:active { background: rgba(232,238,245,0.2); }

      .forge-section h3 {
        margin: 14px 0 8px;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        opacity: 0.7;
      }
      .forge-section h3 small {
        text-transform: none;
        letter-spacing: 0;
        opacity: 0.55;
        font-weight: 400;
      }

      .forge-hero {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 14px;
        align-items: center;
        padding: 14px;
        border-radius: 16px;
        border: 3px solid rgba(255,255,255,0.1);
        background: rgba(255,255,255,0.04);
      }
      .forge-hero.ready {
        border-color: rgba(62, 207, 142, 0.65);
        background: linear-gradient(135deg, rgba(62,207,142,0.18), rgba(255,179,71,0.08));
        box-shadow: 0 0 0 1px rgba(62,207,142,0.15), 0 8px 24px rgba(0,0,0,0.25);
        animation: forge-pulse 1.8s ease-in-out infinite;
      }
      .forge-hero.locked {
        border-color: rgba(255, 179, 71, 0.35);
        background: linear-gradient(135deg, rgba(255,179,71,0.1), rgba(255,255,255,0.03));
      }
      .forge-hero.done {
        border-color: rgba(110, 198, 255, 0.4);
      }
      @keyframes forge-pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.01); }
      }
      .forge-hero-icon {
        width: 104px;
        height: 104px;
        border-radius: 20px;
        background: rgba(0,0,0,0.28);
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid rgba(255,255,255,0.1);
      }
      .forge-hero-body h3 {
        margin: 4px 0 6px;
        font-size: 22px;
        font-weight: 800;
        text-transform: none;
        letter-spacing: 0;
        opacity: 1;
        color: #fff;
      }
      .forge-hero-body p {
        margin: 0 0 10px;
        font-size: 13px;
        line-height: 1.35;
        opacity: 0.75;
      }
      .forge-badge {
        display: inline-block;
        padding: 3px 8px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        background: rgba(255,179,71,0.2);
        color: #ffb347;
      }
      .forge-hero.ready .forge-badge {
        background: rgba(62,207,142,0.25);
        color: #3ecf8e;
      }

      .cost-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 10px;
      }
      .cost-chips.compact { margin-bottom: 8px; justify-content: center; }
      .cost-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px 4px 4px;
        border-radius: 999px;
        background: rgba(0,0,0,0.28);
        border: 1px solid rgba(255,255,255,0.1);
        font-size: 12px;
        font-weight: 700;
      }
      .cost-chip.ok { border-color: rgba(62,207,142,0.45); color: #3ecf8e; }
      .cost-chip.miss { border-color: rgba(255,122,106,0.45); color: #ff7a6a; }
      .coin-chip {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: #ffc850;
        color: #5a3a00;
        font-weight: 800;
        font-size: 13px;
      }

      .forge-progress {
        height: 10px;
        border-radius: 999px;
        background: rgba(0,0,0,0.35);
        overflow: hidden;
        margin-bottom: 6px;
      }
      .forge-progress-bar {
        height: 100%;
        border-radius: inherit;
        background: linear-gradient(90deg, #ffb347, #3ecf8e);
        transition: width 0.25s ease;
      }

      .forge-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
        gap: 10px;
      }
      .forge-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 6px;
        padding: 12px 10px;
        border-radius: 14px;
        border: 2px solid rgba(255,255,255,0.08);
        background: rgba(255,255,255,0.03);
      }
      .forge-card.can {
        border-color: rgba(62,207,142,0.45);
        background: rgba(62,207,142,0.1);
      }
      .forge-card-icon {
        width: 64px;
        height: 64px;
        border-radius: 14px;
        background: rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .forge-card-name {
        font-size: 13px;
        font-weight: 700;
        line-height: 1.2;
      }

      .craft-btn {
        border: none;
        border-radius: 12px;
        background: #3ecf8e;
        color: #062016;
        font: inherit;
        font-size: 14px;
        font-weight: 800;
        padding: 10px 16px;
        cursor: pointer;
        width: 100%;
        -webkit-tap-highlight-color: transparent;
        box-shadow: 0 3px 0 #1f8a5a;
      }
      .craft-btn:active {
        transform: translateY(2px);
        box-shadow: 0 1px 0 #1f8a5a;
      }
      .craft-btn:disabled { opacity: 0.35; cursor: not-allowed; box-shadow: none; }
      .craft-btn-hero {
        font-size: 18px;
        padding: 14px 18px;
        border-radius: 14px;
        background: linear-gradient(180deg, #5ae0a4, #2fbf7a);
      }

      .reason {
        font-size: 12px;
        font-weight: 600;
        color: #ffb080;
        line-height: 1.3;
      }

      .inv-bottom {
        display: grid;
        grid-template-columns: 1.4fr 1fr;
        gap: 12px;
        margin-top: 4px;
      }
      @media (max-width: 640px) {
        .inv-bottom { grid-template-columns: 1fr; }
        .forge-hero { grid-template-columns: 1fr; text-align: center; }
        .forge-hero-icon { margin: 0 auto; }
        .cost-chips { justify-content: center; }
        .inv-title-block h2 { font-size: 24px; }
      }

      .mat-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }
      .mat-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px 6px 6px;
        border-radius: 12px;
        background: rgba(255,255,255,0.05);
        border: 1px solid rgba(255,255,255,0.08);
        font-size: 12px;
      }
      .mat-chip .mat-name { opacity: 0.75; }
      .mat-chip strong { font-size: 13px; }

      .gear-row {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 8px;
      }
      .gear-btn {
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        padding: 8px;
        min-width: 72px;
        border-radius: 12px;
        border: 2px solid rgba(255,255,255,0.1);
        background: rgba(255,255,255,0.04);
        color: inherit;
        font: inherit;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }
      .gear-btn:active { background: rgba(255,255,255,0.12); }

      .inv-hotbar-edit {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
      }
      .hot-edit {
        width: 56px;
        height: 56px;
        border-radius: 12px;
        border: 2px solid rgba(255,255,255,0.12);
        background: rgba(255,255,255,0.04);
        color: inherit;
        font: inherit;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        padding: 4px;
        box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--accent, transparent) 35%, transparent);
        -webkit-tap-highlight-color: transparent;
      }
      .hot-edit.active { border-color: #e8eef5; }
      .hot-edit .key { font-size: 9px; opacity: 0.5; }
      .hot-edit .hot-empty { font-size: 20px; opacity: 0.35; font-weight: 700; }
      .inv-empty { opacity: 0.45; font-size: 13px; padding: 8px 2px; }
    `;
    document.head.appendChild(style);
  }
}
